import React, { useState, useEffect, useMemo } from 'react';

const ImageSlider = ({ content, imageMap }) => {
  // state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliderWidth, setSliderWidth] = useState(null);
  const [naturalDims, setNaturalDims] = useState(null);

  // parse content safely with useMemo so parsing happens deterministically and not conditionally
  const { data, parseError } = useMemo(() => {
    if (!content) return { data: null, parseError: null };
    try {
      const parsed = JSON.parse(content);
      return { data: parsed, parseError: null };
    } catch (e) {
      return { data: null, parseError: 'Error parsing image slider data. Please ensure it is valid JSON.' };
    }
  }, [content]);

  // clamp currentIndex when data changes
  useEffect(() => {
    if (!data || data.length === 0) {
      setCurrentIndex(0);
      return;
    }
    if (currentIndex >= data.length) {
      setCurrentIndex(0);
    }
  }, [data, currentIndex]);

  // derive current item and resolved src using useMemo
  const currentItem = useMemo(() => {
    if (!data || data.length === 0) return null;
    return data[currentIndex] || null;
  }, [data, currentIndex]);

  const resolvedSrc = useMemo(() => {
    if (!currentItem) return null;
    const src = currentItem.image;
    if (!src) return null;

    if (/^https?:/i.test(src) || src.startsWith('/')) return src;
    const normalized = src.startsWith('./') ? src.slice(2) : src;
    if (imageMap && imageMap[normalized]) return imageMap[normalized];
    return src;
  }, [currentItem, imageMap]);

  // helper to compute displayed width from natural dims and viewport
  const computeWidthFromDims = (width, height) => {
    if (!width || !height) return null;
    const maxAvailable = Math.floor(window.innerWidth * 0.92);
    const displayWidth = Math.min(width, maxAvailable);
    const maxHeight = Math.floor(window.innerHeight * 0.78);
    if (height > maxHeight) {
      const scaled = Math.floor((width / height) * maxHeight);
      return String(Math.min(displayWidth, scaled)) + 'px';
    }
    return String(displayWidth) + 'px';
  };

  // Load image once to capture natural dimensions
  useEffect(() => {
    if (!resolvedSrc) {
      setNaturalDims(null);
      setSliderWidth(null);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.src = resolvedSrc;
    img.onload = () => {
      if (cancelled) return;
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      setNaturalDims(dims);
      const w = computeWidthFromDims(dims.width, dims.height);
      setSliderWidth(w);
    };
    img.onerror = () => {
      if (!cancelled) setNaturalDims(null);
      if (!cancelled) setSliderWidth(null);
    };

    return () => { cancelled = true; };
  }, [resolvedSrc]);

  // Recompute on window resize using stored natural dimensions
  useEffect(() => {
    if (!naturalDims) return;
    const onResize = () => {
      const w = computeWidthFromDims(naturalDims.width, naturalDims.height);
      setSliderWidth(w);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [naturalDims]);

  // Now safe to handle early-return cases
  if (parseError) return <div style={{ color: 'red' }}>{parseError}</div>;
  if (!data || data.length === 0) return null;

  const controlsStyle = sliderWidth ? { width: sliderWidth } : { width: 'min(760px, 92%)' };
  const frameStyle = sliderWidth ? { width: sliderWidth } : { width: 'auto' };

  return (
    <div className="image-slider" role="group" aria-label="Training image progression">
      <div className="image-slider-controls" style={controlsStyle}>
        <input
          type="range"
          min={0}
          max={data.length - 1}
          value={currentIndex}
          onChange={(e) => setCurrentIndex(parseInt(e.target.value, 10))}
          className="image-slider-range"
          aria-valuemin={0}
          aria-valuemax={data.length - 1}
          aria-valuenow={currentIndex}
          aria-label="Step selector"
        />

        <div className="image-slider-meta" aria-hidden="true">
          Step {currentIndex + 1} / {data.length}
        </div>
      </div>

      <div className="image-slider-frame" style={frameStyle}>
        {/* persistent badge overlay so the step is always visible */}
        <div className="image-slider-step" aria-hidden="true">{currentItem && currentItem.step ? `Step ${currentItem.step}` : `Step ${currentIndex + 1}`}</div>
        <img
          src={resolvedSrc}
          alt={currentItem ? `Visual progression at step ${currentItem.step}` : 'Training image progression'}
          className="image-slider-img"
        />
      </div>
    </div>
  );
};

export default ImageSlider;
